export interface MobileKeyMessage {
    readonly type: 'key';
    readonly isDown: boolean;
    readonly payload: Record<string, unknown>;
}

export interface MobileMouseMessage {
    readonly type: 'mouse';
    readonly x: number;
    readonly y: number;
}

export interface MobileStatusMessage {
    readonly type: 'mobile_status';
    readonly connected: boolean;
    readonly reconnected?: boolean;
    readonly manualDisconnect?: boolean;
}

export interface MobileSessionClearedMessage {
    readonly type: 'session_cleared';
    readonly reason: string;
}

export type MobileControllerMessage =
    | MobileKeyMessage
    | MobileMouseMessage
    | MobileSessionClearedMessage
    | MobileStatusMessage;

type MessageCallback = (message: MobileControllerMessage) => void;
type ErrorCallback = (message: string) => void;

const MAX_RECONNECT_TIMEOUT_MS = 5000;

export class MobileControllerSessionService {
    private readonly endpoint: string;
    private readonly onMessage: MessageCallback;
    private readonly onProtocolError: ErrorCallback;
    private readonly onSecurityError: ErrorCallback;
    private reconnectAttempts: number;
    private sessionId: string;
    private websocket: WebSocket | null;
    private stopped: boolean;

    constructor(args: {
        endpoint: string;
        onMessage: MessageCallback;
        onProtocolError: ErrorCallback;
        onSecurityError: ErrorCallback;
    }) {
        this.endpoint = args.endpoint;
        this.onMessage = args.onMessage;
        this.onProtocolError = args.onProtocolError;
        this.onSecurityError = args.onSecurityError;
        this.reconnectAttempts = 0;
        this.sessionId = '';
        this.websocket = null;
        this.stopped = true;
    }

    public start(sessionId: string): void {
        this.stop();
        this.stopped = false;
        this.reconnectAttempts = 0;
        this.sessionId = sessionId;
        this.connect();
    }

    public stop(): void {
        this.stopped = true;
        if (!this.websocket) {
            return;
        }

        this.websocket.onopen = null;
        this.websocket.onmessage = null;
        this.websocket.onerror = null;
        this.websocket.onclose = null;
        this.websocket.close();
        this.websocket = null;
    }

    public static generateSessionId(): string {
        const values = new Uint8Array(16);
        window.crypto.getRandomValues(values);
        return Array.from(values)
            .map((value: number) => value.toString(16).padStart(2, '0'))
            .join('');
    }

    private connect(): void {
        if (this.stopped || !this.sessionId) {
            return;
        }

        const websocketUrl = `${this.endpoint}/?session=${encodeURIComponent(this.sessionId)}&type=host`;
        this.websocket = new WebSocket(websocketUrl);

        this.websocket.onopen = () => {
            this.reconnectAttempts = 0;
        };

        this.websocket.onmessage = (event: MessageEvent<unknown>) => {
            this.handleSocketMessage(event);
        };

        this.websocket.onerror = () => {
            this.onSecurityError('Mobile WebSocket error');
        };

        this.websocket.onclose = () => {
            if (this.stopped) {
                return;
            }

            const timeout = Math.min(MAX_RECONNECT_TIMEOUT_MS, (2 ** this.reconnectAttempts) * 500);
            this.reconnectAttempts += 1;
            window.setTimeout(() => {
                this.connect();
            }, timeout);
        };
    }

    private handleSocketMessage(event: MessageEvent<unknown>): void {
        const result = this.parseSocketPayload(event.data);
        if ('error' in result) {
            this.onProtocolError(`Blocked mobile message: ${result.error}`);
            return;
        }

        this.onMessage(result.message);
    }

    private parseSocketPayload(rawPayload: unknown): { message: MobileControllerMessage } | { error: string } {
        if (typeof rawPayload !== 'string') {
            return { error: 'non-string payload' };
        }

        let parsed: Record<string, unknown>;
        try {
            parsed = JSON.parse(rawPayload) as Record<string, unknown>;
        } catch (_error) {
            return { error: 'JSON parse failure' };
        }

        const type = parsed.type;
        if (typeof type !== 'string') {
            return { error: 'missing or invalid type field' };
        }

        if (type === 'key') {
            if (typeof parsed.isDown !== 'boolean') {
                return { error: 'key message missing isDown boolean' };
            }

            if (!parsed.payload || typeof parsed.payload !== 'object') {
                return { error: 'key message missing payload object' };
            }

            return {
                message: {
                    type: 'key',
                    isDown: parsed.isDown,
                    payload: parsed.payload as Record<string, unknown>
                }
            };
        }

        if (type === 'mouse') {
            if (typeof parsed.x !== 'number' || typeof parsed.y !== 'number') {
                return { error: 'mouse message missing x/y numbers' };
            }

            return {
                message: {
                    type: 'mouse',
                    x: parsed.x,
                    y: parsed.y
                }
            };
        }

        if (type === 'mobile_status') {
            if (typeof parsed.connected !== 'boolean') {
                return { error: 'mobile_status message missing connected boolean' };
            }

            return {
                message: {
                    type: 'mobile_status',
                    connected: parsed.connected,
                    reconnected: typeof parsed.reconnected === 'boolean' ? parsed.reconnected : undefined,
                    manualDisconnect: typeof parsed.manualDisconnect === 'boolean' ? parsed.manualDisconnect : undefined
                }
            };
        }

        if (type === 'session_cleared') {
            if (typeof parsed.reason !== 'string') {
                return { error: 'session_cleared message missing reason string' };
            }

            return {
                message: {
                    type: 'session_cleared',
                    reason: parsed.reason
                }
            };
        }

        return { error: `unknown message type: ${type}` };
    }
}
